import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Mock-interview transcription error definitions.
 *
 * Maps backend codes returned by `POST /api/transcribe` (Deepgram) and
 * client-side fallback codes to copy scoped to the *mock interview* flow.
 * Mirrors the per-domain pattern in `practiceErrors.ts` /
 * `subscriptionErrors.ts` so interview copy stays independent of the
 * casual-drill practice flow even though both endpoints share the same
 * backend route.
 *
 * Tone: framed for a mock-interview context — "your answer / your response"
 * rather than "recording", with calmer wording around retries since the
 * user is mid-test.
 */
export const INTERVIEW_TRANSCRIBE_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in apiError.code)
  // ---------------------------------------------------------
  VALIDATION_ERROR: {
    title: 'No answer recorded',
    message:
      "We didn't pick up an answer that time. Please try recording again.",
    action: 'Please try recording again.',
    retryable: true,
    category: 'validation',
  },

  TRANSCRIPTION_INVALID_AUDIO: {
    title: "Couldn't read your answer",
    message:
      'Your answer came through in a format we couldn\'t read. Please try recording again.',
    action: 'Please try recording again.',
    retryable: true,
    category: 'validation',
  },

  TRANSCRIPTION_PAYMENT_REQUIRED: {
    title: 'Voice answers unavailable',
    message:
      "Our transcription service has a billing issue. We've been notified and are looking into it. Please type your answer to keep going.",
    action: 'Please type your answer to keep going.',
    retryable: false,
    category: 'server',
  },

  TRANSCRIPTION_EMPTY_RESULT: {
    title: "Didn't catch your answer",
    message:
      "We couldn't hear your answer. Please speak a little louder and try again.",
    action: 'Please speak louder and try again.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Speak louder and closer to the microphone',
      'Move to a quieter spot',
      "Check that your microphone isn't muted",
    ],
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Take a breath',
    message:
      "Answers are coming in a little fast. Please wait a few seconds and try again.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  TRANSCRIPTION_RATE_LIMITED: {
    title: 'Take a breath',
    message:
      "Answers are coming in a little fast. Please wait a few seconds and try again.",
    action: 'Please wait a few seconds and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  TRANSCRIPTION_AUTH_ERROR: {
    title: 'Service hiccup',
    message:
      "We're having trouble with our transcription service. We've been notified and are looking into it. Please type your answer or try again in a moment.",
    action: 'Please type your answer or try again in a moment.',
    retryable: true,
    retryAfterSeconds: 60,
    category: 'server',
  },

  TRANSCRIPTION_SERVICE_ERROR: {
    title: 'Transcription failed',
    message:
      "Something went wrong while transcribing your answer. We've been notified and are looking into it. Please try again in a moment.",
    action: 'Please try again in a moment.',
    retryable: true,
    category: 'server',
  },

  TRANSCRIPTION_UNAVAILABLE: {
    title: 'Voice answers unavailable',
    message:
      'Voice answers are temporarily unavailable. Please type your answer to keep going.',
    action: 'Please type your answer to keep going.',
    retryable: true,
    retryAfterSeconds: 30,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback code (lowercase, used when apiError.code is missing)
  // ---------------------------------------------------------
  transcription_failed: {
    title: "Couldn't transcribe your answer",
    message:
      'We had trouble reaching our transcription service. Please check your connection and try again.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  // ---------------------------------------------------------
  // Offline (returned regardless of input code when offline)
  // ---------------------------------------------------------
  network_offline: {
    title: "You're offline",
    message:
      'Voice answers need an internet connection. Please reconnect to continue your interview.',
    action: 'Please reconnect to continue your interview.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a mock-interview transcription error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getInterviewTranscribeError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      ...INTERVIEW_TRANSCRIBE_ERRORS.network_offline,
      code: 'network_offline',
    };
  }

  const errorDef = INTERVIEW_TRANSCRIBE_ERRORS[code] ?? {
    title: "Couldn't transcribe your answer",
    message:
      "Something went wrong while transcribing your answer. We've been notified and are looking into it. Please try again or type your answer.",
    action: 'Please try again or type your answer.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}
